import { createReducer, createAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall } from '@reducers/utils';
import type { AppThunk } from '@store/types';
import type { PartsState, PartSearchResponse, GetPartsOptions, GetPartsByIdParams } from './types';

// Re-export types for consumers
export type { PartsState, PartSearchResponse, GetPartsOptions, GetPartsByIdParams } from './types';

// === Initial State ===

const initState: PartsState = {
  loaded: false,
  processing: false,
  canUpload: true,
  data: {
    parts: [],
    totalResultCount: 0,
  },
  page: 0,
  showLoading: true,
  isError: false,
  pageSize: 100,
  pagination: {
    HasNext: true,
  },
};

// === Actions ===

export const set = createAction<Partial<PartsState>>('parts/set');
export const updatePage = createAction('parts/updatePage');
export const resetPage = createAction('parts/resetPage');
export const setError = createAction('parts/setError');
export const clearParts = createAction('parts/clearParts');
export const clear = createAction('clear');

// === Thunks ===

export const getPartsSearch =
  (searchValue: string): AppThunk<Promise<PartSearchResponse | undefined>> =>
  async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.productionOrders.searchParts, {
          params: {
            search: searchValue,
            top: 100,
          },
        })
      )
    );
  };

export const getPartByID =
  (id: string | number): AppThunk<Promise<PartSearchResponse | undefined>> =>
  async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.productionOrders.searchParts, {
          params: {
            oDataFilter: `PartId eq ${id}`,
          },
        })
      )
    );
  };

export const getPartsByID =
  ({ ids }: GetPartsByIdParams): AppThunk<Promise<PartSearchResponse | undefined>> =>
  async (dispatch) => {
    return await dispatch(
      backendCall(
        createApiCall((api) => api.productionOrders.searchParts, {
          params: {
            oDataFilter: 'PartId eq ' + ids.join(' or PartId eq '),
          },
        })
      )
    );
  };

export const getParts =
  (callback: (() => void) | undefined, { orderBy, orderByDirection, resetPages, searchValue, resetRecords }: GetPartsOptions): AppThunk<Promise<PartSearchResponse | undefined>> =>
  async (dispatch, getState) => {
    if (resetPages) {
      await dispatch(resetPage());
    } else {
      await dispatch(updatePage());
    }

    const {
      parts: { page, pageSize, data },
    } = getState();

    return await dispatch(
      backendCall(
        createApiCall((api) => api.productionOrders.searchParts, {
          params: {
            search: searchValue,
            top: pageSize,
            skip: page * pageSize,
            orderBy: [`${orderBy} ${orderByDirection}`],
          },
          onSuccess: (d: PartSearchResponse) => {
            let parts;
            if (resetRecords) {
              parts = d.searchResult;
            } else {
              parts = [...data.parts, ...(d.searchResult || [])];
            }
            dispatch(set({ data: { parts: parts || [], totalResultCount: d.totalResultCount || 0 } }));
            if (callback) {
              callback();
            }
          },
          onError: (error: unknown) => {
            console.log(error, 'error');
            dispatch(setError());
          },
        })
      )
    );
  };

// === Reducer ===

export default createReducer<PartsState>(JSON.parse(JSON.stringify(initState)), (builder) => {
  builder
    .addCase(set, (state, action) => ({
      ...state,
      loaded: true,
      processing: false,
      ...action.payload,
    }))
    .addCase(updatePage, (state) => ({
      ...state,
      page: state.page + 1,
      processing: true,
    }))
    .addCase(resetPage, (state) => ({
      ...state,
      page: 0,
      processing: true,
    }))
    .addCase(setError, (state) => ({
      ...state,
      isError: true,
    }))
    .addCase(clearParts, () => initState)
    .addCase(clear, () => initState);
});
